import { useState } from "react";
import {
  Notification,
  NotificationPriority,
  NotificationType,
} from "../../types/notification";
import { markAsRead } from "../../api/notification.api";
import { useQueryClient } from "@tanstack/react-query";
import { notificationKeys } from "../../queryKeys";

const notificationIcon: Record<NotificationType, string> = {
  ORDER_PAYMENT_SUCCESS: "💰",
  ORDER_CREATED: "📦",
  INVENTORY_LOW: "⚠️",
  OUT_OF_STOCK: "❌",
  SYSTEM: "🔔",
};

const priorityStyles: Record<NotificationPriority, string> = {
  HIGH: "text-red-600 bg-red-50",
  MEDIUM: "text-amber-600 bg-amber-50",
  LOW: "text-blue-600 bg-blue-50",
};

export const NotificationCard = ({ note }: { note: Notification }) => {
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState(false);
  const [isRead, setIsRead] = useState(note.is_read);

  const unread = !isRead;

  const toggle = async () => {
    setExpanded((prev) => !prev);

    // ✅ Mark as read only once
    if (!isRead) {
      try {
        await markAsRead(note.SK);
        setIsRead(true);
        queryClient.invalidateQueries({ queryKey: notificationKeys.unread });
      } catch (error) {
        console.error(error, "mark as read error");
      }
    }
  };

  return (
    <div
      className={`rounded-2xl border transition-all cursor-pointer 
      hover:shadow-md active:scale-[0.99]
      ${
        unread
          ? "bg-emerald-50 border-emerald-200 ring-1 ring-emerald-500/20"
          : "bg-white border-slate-100 opacity-70"
      }`}
    >
      {/* HEADER */}
      <div onClick={toggle} className="p-5 flex items-start gap-4">
        {/* Icon */}
        <div
          className={`p-3 rounded-xl text-lg ${
            unread ? "bg-emerald-100" : "bg-slate-50"
          }`}
        >
          {notificationIcon[note.type]}
        </div>

        {/* Main content */}
        <div className="flex-1">
          <div className="flex justify-between gap-3">
            <div>
              <p
                className={`font-bold ${
                  unread ? "text-slate-900" : "text-slate-500"
                }`}
              >
                {note.title}
              </p>

              <p
                className={`text-sm ${
                  unread ? "text-slate-700" : "text-slate-400"
                } line-clamp-1`}
              >
                {note.message}
              </p>
            </div>

            {/* Time */}
            <p className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">
              {new Date(note.created_at).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>

        {/* Arrow */}
        <div className="text-slate-400 text-sm">{expanded ? "▲" : "▼"}</div>

        {/* Unread Dot */}
        {unread && (
          <span className="w-2 h-2 rounded-full bg-emerald-500 mt-2 animate-pulse"></span>
        )}
      </div>

      {/* EXPANDED */}
      {expanded && (
        <div className="border-t border-slate-100 px-6 pb-5 pt-4 space-y-4 animate-in fade-in">
          <p className="text-sm text-slate-700">{note.message}</p>

          {note.metadata && (
            <div className="space-y-1 text-xs text-slate-500">
              {note.metadata.order_id && (
                <p>
                  <span className="font-semibold text-slate-700">
                    Order ID:
                  </span>{" "}
                  {note.metadata.order_id}
                </p>
              )}

              {note.metadata.amount && (
                <p>
                  <span className="font-semibold text-slate-700">Amount:</span>{" "}
                  ₹{note.metadata.amount}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between items-center flex-wrap gap-3">
            <span
              className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${priorityStyles[note.priority]}`}
            >
              {note.priority} Priority
            </span>

            <p className="text-xs text-slate-400 font-semibold">
              Ref: {note.reference_id}
            </p>
          </div>

          <p className="text-[11px] text-slate-400">
            {new Date(note.created_at).toLocaleString("en-IN")}
          </p>
        </div>
      )}
    </div>
  );
};
